import fetchApi from '@/lib/tools/api'
import FeedbackIcon from '@mui/icons-material/Feedback'
import {Box, Typography} from '@mui/material'
import {CategoryScale, Chart as ChartJS, Legend, LinearScale, LineElement, PointElement, Title, Tooltip} from 'chart.js'
import {Dayjs} from 'dayjs'
import React, {useEffect, useState} from 'react'
import {Line} from 'react-chartjs-2'
import {Link} from 'react-router-dom'
import CardWithIcon from './CardWithIcon'
import PeriodSelector from './PeriodSelector'

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend
)

interface ContributionTerroirsStats {
	data: {
		period: string
		terroirs: number
	}[]
	period_type?: 'days' | 'months'
	period_value?: number
	period_months?: number // backward compatibility
}

const TotalContributions = () => {
	const [stats, setStats] = useState<ContributionTerroirsStats | null>(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)
	const [selectedPeriod, setSelectedPeriod] = useState<'30d' | '90d' | '12m' | '24m' | 'custom'>('30d')
	const [startDate, setStartDate] = useState<Dayjs>()
	const [endDate, setEndDate] = useState<Dayjs>()

	const fetchStats = async (period: '30d' | '90d' | '12m' | '24m' | 'custom', customStartDate?: Dayjs, customEndDate?: Dayjs) => {
		try {
			setLoading(true)
			let url = '/stats/contributions-terroirs'

			if (period === 'custom' && customStartDate && customEndDate) {
				url += `?start_date=${customStartDate.format('YYYY-MM-DD')}&end_date=${customEndDate.format('YYYY-MM-DD')}`
			} else {
				const days = period === '30d' ? 30 : period === '90d' ? 90 : period === '12m' ? 365 : 730 // 24m = 730 days
				url += `?days=${days}`
			}

			const result = await fetchApi<ContributionTerroirsStats>(url)
			setStats(result)
			setError(null)
		} catch (err) {
			console.error('❌ TotalContributions: Error fetching stats:', err)
			setError('Erreur lors du chargement des statistiques')
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		fetchStats(selectedPeriod, startDate, endDate)
	}, [])

	const handlePeriodChange = (period: '30d' | '90d' | '12m' | '24m' | 'custom', customStartDate?: Dayjs, customEndDate?: Dayjs) => {
		setSelectedPeriod(period)
		setStartDate(customStartDate)
		setEndDate(customEndDate)
		fetchStats(period, customStartDate, customEndDate)
	}

	if (loading) {
		return (
			<CardWithIcon
				icon={FeedbackIcon}
				title={'Contributions terroirs'}
				subtitle="Chargement..."
			>
				<Box sx={{
					height: '180px',
					padding: '20px',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center'
				}}>
					<Typography variant="body2">Chargement des statistiques...</Typography>
				</Box>
			</CardWithIcon>
		)
	}

	if (error) {
		return (
			<CardWithIcon
				icon={FeedbackIcon}
				title={'Contributions terroirs'}
				subtitle="Erreur"
			>
				<Box sx={{
					height: '180px',
					padding: '20px',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center'
				}}>
					<Typography variant="body2" color="error">{error}</Typography>
				</Box>
			</CardWithIcon>
		)
	}

	const totalContributions = stats?.data.reduce((sum, item) => sum + item.terroirs, 0) || 0

	const chartData = {
		labels: stats?.data.map(d => d.period) || [],
		datasets: [
			{
				label: 'Terroirs',
				data: stats?.data.map(d => d.terroirs) || [],
				borderColor: '#2e7d32',
				backgroundColor: (context: any) => {
					const ctx = context.chart.ctx
					const gradient = ctx.createLinearGradient(0, 0, 0, 200)
					gradient.addColorStop(0, 'rgba(46, 125, 50, 0.2)')
					gradient.addColorStop(1, 'rgba(46, 125, 50, 0.02)')
					return gradient
				},
				borderWidth: 2,
				tension: 0.4,
				pointRadius: 2,
				pointHoverRadius: 5,
				pointBackgroundColor: '#2e7d32',
				pointBorderColor: '#ffffff',
				pointBorderWidth: 2,
				fill: true
			}
		]
	}

	const options = {
		responsive: true,
		maintainAspectRatio: false,
		interaction: {
			intersect: false,
			mode: 'index' as const
		},
		plugins: {
			legend: {
				display: false // On cache la légende pour gagner de la place
			},
			tooltip: {
				backgroundColor: 'rgba(255, 255, 255, 0.95)',
				titleColor: '#1e293b',
				bodyColor: '#475569',
				borderColor: '#e2e8f0',
				borderWidth: 1,
				cornerRadius: 8,
				padding: 12,
				boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
			}
		},
		scales: {
			x: {
				grid: {
					display: false
				},
				border: {
					display: false
				},
				ticks: {
					font: {
						size: 9,
						family: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
					},
					color: '#94a3b8',
					maxRotation: 0,
					padding: 8,
					maxTicksLimit: 5
				}
			},
			y: {
				grid: {
					color: 'rgba(148, 163, 184, 0.08)',
					drawBorder: false
				},
				border: {
					display: false
				},
				ticks: {
					font: {
						size: 9,
						family: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
					},
					color: '#94a3b8',
					padding: 8
				},
				beginAtZero: true
			}
		},
		elements: {
			line: {
				borderCapStyle: 'round' as const,
				borderJoinStyle: 'round' as const
			}
		}
	}

	return (
		<CardWithIcon
			icon={FeedbackIcon}
			title={'Contributions terroirs'}
			subtitle={`${totalContributions}`}
		>
			<Box sx={{height: 'auto', padding: '20px'}}>
				<Box sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: '5px'
				}}>
					<Typography variant="caption">
						Évolution {stats?.period_value} derniers {stats?.period_type === 'days' ? 'jours' : 'mois'}
					</Typography>
					<PeriodSelector
						selectedPeriod={selectedPeriod}
						startDate={startDate}
						endDate={endDate}
						onPeriodChange={handlePeriodChange}
					/>
				</Box>
				<div style={{height: '155px', width: '100%'}}>
					<Line data={chartData} options={options}/>
				</div>
				<Link to="/contributions">
					<Typography variant="caption">
						Voir toutes les contributions
					</Typography>
				</Link>
			</Box>
		</CardWithIcon>
	)
}

export default TotalContributions
